import { SignContractBody } from "../../contracts/schema";

async function sha256Base64(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", enc);
  const bytes = new Uint8Array(digest);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++)
    binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export async function buildSignContractBody({
  values,
  slug,
  identityVerifiedTimestamp,
  identityVerificationType,
}: {
  values: Record<string, any>;
  slug?: string;
  identityVerifiedTimestamp?: string;
  identityVerificationType?: string;
}): Promise<SignContractBody> {
  const sessionId =
    slug ||
    (typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : String(Date.now()));
  const contentHash = await sha256Base64(JSON.stringify(values));
  const acknowledgements: string[] = [];
  if (values.ack_revocation) acknowledgements.push("ack_revocation");
  if (values.ack_redisclosure) acknowledgements.push("ack_redisclosure");
  if (values.ack_electronic_delivery)
    acknowledgements.push("ack_electronic_delivery");
  if (values.ack_conditions) acknowledgements.push("ack_conditions");

  const tz = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  })();

  return {
    sessionId,
    contentHash,
    version: "hipaa-v1",
    formValues: values,
    acknowledgements:
      acknowledgements.length > 0 && typeof acknowledgements[0] === "string"
        ? ([acknowledgements[0], ...acknowledgements.slice(1)] as [
            string,
            ...string[]
          ])
        : (["acknowledged"] as [string]),
    signature: {
      base64Png: String(values.signature_data_url || ""),
    },
    consent: true,
    signerName: String(values.patient_full_name || "").trim(),
    signerEmail: values.patient_email ? String(values.patient_email) : null,
    clientTimestamp: new Date().toISOString(),
    clientTimezone: tz,
    ...(identityVerifiedTimestamp ? { identityVerifiedTimestamp } : {}),
    ...(identityVerificationType ? { identityVerificationType } : {}),
  };
}
